import { 
  calculateMaterialRequirements, 
  checkRequirementsAvailability,
  findAvailableItems 
} from './inventoryCalculations';
import { getTotalSoldQuantities } from '../firebase/soldMaterialsService';
import { getInventoryItems } from '../firebase/inventoryService';

/**
 * Enhanced Material Availability System
 * Implements time-based material allocation with sold materials tracking
 */

// Material availability statuses
export const AVAILABILITY_STATUS = {
  CONFIRMED_AVAILABLE: 'confirmed_available',      // Schedule dates set, materials available
  PRELIMINARY_AVAILABLE: 'preliminary_available',  // Invoice dates only, materials available  
  SCHEDULE_PENDING: 'schedule_pending',           // No dates specified
  CONFIRMED_CONFLICT: 'confirmed_conflict',       // Schedule dates set, materials unavailable
  PRELIMINARY_CONFLICT: 'preliminary_conflict',   // Invoice dates only, materials unavailable
  ERROR: 'error'                                 // Error during availability check
};

export const AVAILABILITY_STATUS_LABELS = {
  [AVAILABILITY_STATUS.CONFIRMED_AVAILABLE]: 'Available',
  [AVAILABILITY_STATUS.PRELIMINARY_AVAILABLE]: 'Available',
  [AVAILABILITY_STATUS.SCHEDULE_PENDING]: 'Schedule Pending',
  [AVAILABILITY_STATUS.CONFIRMED_CONFLICT]: 'Conflict',
  [AVAILABILITY_STATUS.PRELIMINARY_CONFLICT]: 'Conflict',
  [AVAILABILITY_STATUS.ERROR]: 'Check Failed'
};

export const AVAILABILITY_STATUS_COLORS = {
  [AVAILABILITY_STATUS.CONFIRMED_AVAILABLE]: '#52c41a',     // Green
  [AVAILABILITY_STATUS.PRELIMINARY_AVAILABLE]: '#52c41a',   // Green
  [AVAILABILITY_STATUS.SCHEDULE_PENDING]: '#faad14',        // Orange
  [AVAILABILITY_STATUS.CONFIRMED_CONFLICT]: '#ff4d4f',      // Red
  [AVAILABILITY_STATUS.PRELIMINARY_CONFLICT]: '#ff4d4f',    // Red
  [AVAILABILITY_STATUS.ERROR]: '#d9d9d9'                   // Light Gray
};

/**
 * Extract effective dates from invoice and schedules using dual-date system
 * @param {Object} invoice - Invoice object
 * @param {Array} relatedSchedules - Related schedules array
 * @returns {Object} Effective dates with priority information
 */
export const getEffectiveDates = (invoice, relatedSchedules = []) => {
  console.log('🔍 Getting effective dates:', {
    invoiceId: invoice?.id,
    invoiceNumber: invoice?.invoiceNumber,
    schedulesCount: relatedSchedules?.length || 0,
    invoiceDates: invoice?.dates
  });

  // Priority 1: Use scheduled dates if available (actual operational dates)
  const scheduledDates = relatedSchedules
    .filter(schedule => schedule.scheduledDate)
    .map(schedule => ({
      startDate: schedule.scheduledDate,
      endDate: schedule.scheduledDate, // Single day for now, can be enhanced
      source: 'scheduled',
      scheduleId: schedule.id,
      jobType: schedule.jobType
    }));

  if (scheduledDates.length > 0) {
    console.log('✅ Using scheduled dates (Priority 1):', scheduledDates);
    return {
      dates: scheduledDates,
      source: 'scheduled',
      priority: 1,
      isConfirmed: true
    };
  }

  // Priority 2: Use invoice dates (billing dates, preliminary)
  const invoiceDates = extractDatesFromInvoice(invoice);
  
  if (invoiceDates.length > 0) {
    console.log('📅 Using invoice dates (Priority 2):', invoiceDates);
    return {
      dates: invoiceDates,
      source: 'invoice',
      priority: 2,
      isConfirmed: false
    };
  }

  console.log('❌ No dates available');
  return {
    dates: [],
    source: 'none',
    priority: 0,
    isConfirmed: false
  };
};

/**
 * Extract dates from invoice using unified date system
 * @param {Object} invoice - Invoice object
 * @returns {Array} Array of date objects
 */
export const extractDatesFromInvoice = (invoice) => {
  const dates = [];

  if (!invoice) {
    console.warn('⚠️ No invoice provided to extractDatesFromInvoice');
    return dates;
  }

  // Use unified dates array if available
  if (invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0) {
    console.log('📅 Processing unified dates array:', invoice.dates);

    invoice.dates.forEach((dateEntry, index) => {
      switch (dateEntry.type) {
        case 'date':
          if (dateEntry.value) {
            dates.push({
              startDate: dateEntry.value,
              endDate: dateEntry.value,
              source: 'invoice_unified',
              type: 'single',
              index
            });
          }
          break;

        case 'range':
          if (dateEntry.value && dateEntry.endValue) {
            dates.push({
              startDate: dateEntry.value,
              endDate: dateEntry.endValue,
              source: 'invoice_unified',
              type: 'range',
              index
            });
          }
          break;

        case 'tbc':
          // TBC dates can't be used for availability checking
          console.log('⏭️ Skipping TBC date entry');
          break;

        default:
          console.warn(`⚠️ Unknown date entry type: ${dateEntry.type}`);
      }
    });
  } else {
    // Fallback to legacy date fields
    console.log('📅 Using legacy date fields');

    switch (invoice.invoiceType) {
      case 'rental':
      case 'rental_extension':
        if (invoice.rentalStartDate && invoice.rentalEndDate) {
          dates.push({
            startDate: invoice.rentalStartDate,
            endDate: invoice.rentalEndDate,
            source: 'invoice_legacy',
            type: 'range'
          });
        }
        break;

      case 'purchase':
        if (invoice.leadTime) {
          dates.push({
            startDate: invoice.leadTime,
            endDate: invoice.leadTime,
            source: 'invoice_legacy',
            type: 'single'
          });
        }
        break;

      case 'scaffolding_service':
        if (invoice.serviceDate) {
          dates.push({
            startDate: invoice.serviceDate,
            endDate: invoice.serviceDate,
            source: 'invoice_legacy',
            type: 'single'
          });
        }
        break;

      case 'damaged_items':
        // Damaged items don't need availability checking
        console.log('⏭️ Damaged items invoice - no dates needed');
        break;

      default:
        console.warn(`⚠️ Unknown invoice type: ${invoice.invoiceType}`);
    }
  }

  console.log(`📊 Extracted ${dates.length} date(s) from invoice:`, dates);
  return dates;
};

/**
 * Calculate available materials with sold materials tracking
 * Available = Total - Sold - Reserved - Unavailable - Damaged
 * @param {string} materialType - Material type
 * @param {string} startDate - Start date for availability check
 * @param {string} endDate - End date for availability check
 * @param {Array} allInvoices - All invoices for reservation calculation
 * @returns {Promise<Object>} Availability calculation result
 */
export const calculateMaterialAvailability = async (materialType, startDate, endDate, allInvoices = []) => {
  try {
    console.log(`📊 Calculating availability for ${materialType} from ${startDate} to ${endDate}`);
    
    // Get current inventory
    const allInventory = await getInventoryItems();
    const inventory = allInventory.find(inv => inv.materialType === materialType);
    
    if (!inventory) {
      return {
        available: 0,
        total: 0,
        sold: 0,
        reserved: 0,
        unavailable: 0,
        damaged: 0,
        breakdown: {
          new: 0,
          used: 0,
          damaged: 0
        }
      };
    }
    
    // Get sold quantities
    const soldQuantities = await getTotalSoldQuantities(materialType);
    const soldQuantity = soldQuantities[materialType] || 0;
    
    // Calculate base quantities (after sold materials removed)
    const totalQuantity = (inventory.newQuantity || 0) + (inventory.usedQuantity || 0) + (inventory.damagedQuantity || 0);
    const usableQuantity = (inventory.newQuantity || 0) + (inventory.usedQuantity || 0) - soldQuantity;
    const damagedQuantity = inventory.damagedQuantity || 0;
    
    // Calculate reserved materials (future dates)
    const reservedQuantity = await calculateReservedMaterials(materialType, startDate, endDate, allInvoices);
    
    // Calculate unavailable materials (ongoing jobs + damaged)
    const unavailableQuantity = await calculateUnavailableMaterials(materialType, startDate, endDate, allInvoices) + damagedQuantity;
    
    // Final available calculation
    const availableQuantity = Math.max(0, usableQuantity - reservedQuantity - unavailableQuantity);
    
    const result = {
      available: availableQuantity,
      total: totalQuantity,
      sold: soldQuantity,
      reserved: reservedQuantity,
      unavailable: unavailableQuantity - damagedQuantity, // Exclude damaged from unavailable for display
      damaged: damagedQuantity,
      breakdown: {
        new: Math.max(0, (inventory.newQuantity || 0) - Math.floor(soldQuantity * 0.7)), // Assume 70% sold from new
        used: Math.max(0, (inventory.usedQuantity || 0) - Math.floor(soldQuantity * 0.3)), // Assume 30% sold from used
        damaged: damagedQuantity
      }
    };
    
    console.log(`✅ Availability calculated for ${materialType}:`, result);
    return result;
    
  } catch (error) {
    console.error(`❌ Error calculating availability for ${materialType}:`, error);
    return {
      available: 0,
      total: 0,
      sold: 0,
      reserved: 0,
      unavailable: 0,
      damaged: 0,
      breakdown: { new: 0, used: 0, damaged: 0 },
      error: error.message
    };
  }
};

/**
 * Calculate reserved materials (future rental/purchase dates)
 * @param {string} materialType - Material type
 * @param {string} checkStartDate - Check start date
 * @param {string} checkEndDate - Check end date
 * @param {Array} allInvoices - All invoices
 * @returns {Promise<number>} Reserved quantity
 */
const calculateReservedMaterials = async (materialType, checkStartDate, checkEndDate, allInvoices) => {
  try {
    let reservedQuantity = 0;
    const today = new Date().toISOString().split('T')[0];
    
    for (const invoice of allInvoices) {
      if (invoice.status === 'cancelled') continue;
      
      // Skip if no line items or material not in this invoice
      if (!invoice.lineItems || !Array.isArray(invoice.lineItems)) continue;
      
      const materialItem = invoice.lineItems.find(item => item.materialType === materialType);
      if (!materialItem || !materialItem.quantity) continue;
      
      let invoiceStartDate, invoiceEndDate;
      
      // Extract dates based on invoice type
      switch (invoice.invoiceType) {
        case 'rental':
        case 'rental_extension':
          invoiceStartDate = invoice.rentalStartDate;
          invoiceEndDate = invoice.rentalEndDate;
          break;
        case 'purchase':
          invoiceStartDate = invoice.leadTime;
          invoiceEndDate = invoice.leadTime; // Single day for purchase
          break;
        default:
          continue;
      }
      
      if (!invoiceStartDate) continue;
      
      // Check if this is a future reservation (starts after today)
      if (invoiceStartDate > today) {
        // Check if it overlaps with our check period
        if (invoiceStartDate <= checkEndDate && (invoiceEndDate || invoiceStartDate) >= checkStartDate) {
          reservedQuantity += materialItem.quantity;
          console.log(`📅 Reserved: ${materialItem.quantity} ${materialType} for invoice ${invoice.invoiceNumber} (${invoiceStartDate} to ${invoiceEndDate || invoiceStartDate})`);
        }
      }
    }
    
    return reservedQuantity;
  } catch (error) {
    console.error('❌ Error calculating reserved materials:', error);
    return 0;
  }
};

/**
 * Calculate unavailable materials (ongoing jobs)
 * @param {string} materialType - Material type
 * @param {string} checkStartDate - Check start date
 * @param {string} checkEndDate - Check end date
 * @param {Array} allInvoices - All invoices
 * @returns {Promise<number>} Unavailable quantity
 */
const calculateUnavailableMaterials = async (materialType, checkStartDate, checkEndDate, allInvoices) => {
  try {
    let unavailableQuantity = 0;
    const today = new Date().toISOString().split('T')[0];
    
    for (const invoice of allInvoices) {
      if (invoice.status === 'cancelled') continue;
      
      // Skip if no line items or material not in this invoice
      if (!invoice.lineItems || !Array.isArray(invoice.lineItems)) continue;
      
      const materialItem = invoice.lineItems.find(item => item.materialType === materialType);
      if (!materialItem || !materialItem.quantity) continue;
      
      let invoiceStartDate, invoiceEndDate;
      
      // Extract dates based on invoice type
      switch (invoice.invoiceType) {
        case 'rental':
          invoiceStartDate = invoice.rentalStartDate;
          invoiceEndDate = invoice.rentalEndDate;
          break;
        case 'rental_extension':
          invoiceStartDate = invoice.extensionStartDate || invoice.rentalStartDate;
          invoiceEndDate = invoice.extensionEndDate || invoice.rentalEndDate;
          break;
        default:
          continue; // Purchase invoices don't make materials unavailable
      }
      
      if (!invoiceStartDate || !invoiceEndDate) continue;
      
      // Check if this is an ongoing job (started but not ended)
      if (invoiceStartDate <= today && invoiceEndDate >= today) {
        // Check if it overlaps with our check period
        if (invoiceStartDate <= checkEndDate && invoiceEndDate >= checkStartDate) {
          unavailableQuantity += materialItem.quantity;
          console.log(`🔄 Unavailable: ${materialItem.quantity} ${materialType} for ongoing job ${invoice.invoiceNumber} (${invoiceStartDate} to ${invoiceEndDate})`);
        }
      }
    }
    
    return unavailableQuantity;
  } catch (error) {
    console.error('❌ Error calculating unavailable materials:', error);
    return 0;
  }
};

/**
 * Smart availability checking with new material calculation system
 * @param {Object} materialRequirements - Material requirements object
 * @param {Object} invoice - Invoice object
 * @param {Array} schedules - Related schedules array
 * @param {Array} allInvoices - All invoices for calculation
 * @returns {Promise<Object>} Enhanced availability result
 */
export const checkSmartAvailability = async (materialRequirements, invoice, schedules = [], allInvoices = []) => {
  try {
    console.log('🔍 Starting smart availability check:', {
      invoiceId: invoice?.id,
      invoiceNumber: invoice?.invoiceNumber,
      materialTypes: Object.keys(materialRequirements || {}),
      schedulesCount: schedules?.length || 0
    });

    // Validate inputs
    if (!materialRequirements || Object.keys(materialRequirements).length === 0) {
      console.log('⚠️ No material requirements provided');
      return {
        status: AVAILABILITY_STATUS.SCHEDULE_PENDING,
        message: 'No material requirements to check',
        details: {},
        checkedAt: new Date()
      };
    }

    // Get effective dates using dual-date system
    const effectiveDates = getEffectiveDates(invoice, schedules);

    if (effectiveDates.dates.length === 0) {
      console.log('⚠️ No dates available for availability checking');
      return {
        status: AVAILABILITY_STATUS.SCHEDULE_PENDING,
        message: 'Schedule pending - no dates specified',
        details: { effectiveDates },
        checkedAt: new Date()
      };
    }

    // Check availability for each material type
    const materialAvailability = {};
    let overallAvailable = true;
    const conflictDetails = [];

    for (const [materialType, requirement] of Object.entries(materialRequirements)) {
      const requiredQuantity = requirement.totalQuantity || requirement.quantity || 0;
      
      // Use the first date range for availability calculation
      const dateRange = effectiveDates.dates[0];
      
      const availability = await calculateMaterialAvailability(
        materialType,
        dateRange.startDate,
        dateRange.endDate,
        allInvoices
      );
      
      materialAvailability[materialType] = {
        required: requiredQuantity,
        ...availability,
        isAvailable: availability.available >= requiredQuantity,
        shortage: Math.max(0, requiredQuantity - availability.available)
      };
      
      if (availability.available < requiredQuantity) {
        overallAvailable = false;
        conflictDetails.push({
          materialType,
          required: requiredQuantity,
          available: availability.available,
          shortage: requiredQuantity - availability.available
        });
      }
    }

    // Determine status based on date source and availability
    let status;
    let message;

    if (effectiveDates.source === 'scheduled') {
      // Using scheduled dates (confirmed operational dates)
      if (overallAvailable) {
        status = AVAILABILITY_STATUS.CONFIRMED_AVAILABLE;
        message = 'All materials available for scheduled dates';
      } else {
        status = AVAILABILITY_STATUS.CONFIRMED_CONFLICT;
        message = `Material conflicts detected: ${conflictDetails.map(c => `${c.materialType} (need ${c.shortage} more)`).join(', ')}`;
      }
    } else if (effectiveDates.source === 'invoice') {
      // Using invoice dates (preliminary billing dates)
      if (overallAvailable) {
        status = AVAILABILITY_STATUS.PRELIMINARY_AVAILABLE;
        message = 'Materials available for invoice dates';
      } else {
        status = AVAILABILITY_STATUS.PRELIMINARY_CONFLICT;
        message = `Material conflicts detected: ${conflictDetails.map(c => `${c.materialType} (need ${c.shortage} more)`).join(', ')}`;
      }
    } else {
      status = AVAILABILITY_STATUS.SCHEDULE_PENDING;
      message = 'Schedule pending - no dates specified';
    }

    const result = {
      status,
      message,
      isAvailable: overallAvailable,
      effectiveDates,
      materialAvailability,
      conflictDetails,
      details: {
        totalMaterialTypes: Object.keys(materialRequirements).length,
        dateRangesChecked: effectiveDates.dates.length,
        hasConflicts: !overallAvailable,
        source: effectiveDates.source,
        priority: effectiveDates.priority
      },
      checkedAt: new Date()
    };

    console.log('✅ Smart availability check completed:', {
      status,
      isAvailable: overallAvailable,
      source: effectiveDates.source,
      conflicts: conflictDetails.length
    });

    return result;

  } catch (error) {
    console.error('❌ Error in smart availability check:', error);
    return {
      status: AVAILABILITY_STATUS.ERROR,
      message: `Availability check failed: ${error.message}`,
      isAvailable: false,
      error: error.message,
      checkedAt: new Date()
    };
  }
};

/**
 * Generate availability suggestions when conflicts are detected
 * @param {Object} availabilityResult - Result from checkSmartAvailability
 * @param {Object} materialRequirements - Material requirements object
 * @returns {Promise<Array>} Array of suggestions
 */
export const generateAvailabilitySuggestions = async (availabilityResult, materialRequirements) => {
  const suggestions = [];

  if (!availabilityResult || availabilityResult.isAvailable) {
    return suggestions; // No suggestions needed if available
  }

  try {
    // Analyze conflicts and generate suggestions
    for (const result of availabilityResult.availabilityResults || []) {
      if (!result.availability.isAvailable) {
        const { shortages } = result.availability;

        for (const shortage of shortages) {
          // Suggest alternative dates
          const alternativeDates = await findAlternativeDates(
            shortage.materialType,
            shortage.required,
            result.dateRange
          );

          if (alternativeDates.length > 0) {
            suggestions.push({
              type: 'alternative_dates',
              materialType: shortage.materialType,
              shortage: shortage.shortage,
              alternatives: alternativeDates,
              message: `Consider moving ${shortage.materialType} to: ${alternativeDates.slice(0, 3).join(', ')}`
            });
          }

          // Suggest material alternatives (from existing system)
          const materialAlternatives = result.availability.suggestions || [];
          suggestions.push(...materialAlternatives.map(alt => ({
            type: 'material_alternative',
            ...alt
          })));
        }
      }
    }

    return suggestions;
  } catch (error) {
    console.error('❌ Error generating availability suggestions:', error);
    return [];
  }
};

/**
 * Find alternative dates when materials are not available
 * @param {string} materialType - Material type to check
 * @param {number} quantity - Required quantity
 * @param {Object} originalDateRange - Original date range that had conflicts
 * @returns {Promise<Array>} Array of alternative date strings
 */
export const findAlternativeDates = async (materialType, quantity, originalDateRange) => {
  const alternatives = [];

  try {
    // Check dates around the original range
    const originalStart = new Date(originalDateRange.startDate);
    const originalEnd = new Date(originalDateRange.endDate);
    const rangeDays = Math.ceil((originalEnd - originalStart) / (1000 * 60 * 60 * 24)) + 1;

    // Check 2 weeks before and after
    for (let offset = -14; offset <= 14; offset++) {
      if (offset === 0) continue; // Skip original dates

      const testStart = new Date(originalStart);
      testStart.setDate(testStart.getDate() + offset);
      
      const testEnd = new Date(testStart);
      testEnd.setDate(testEnd.getDate() + rangeDays - 1);

      const testStartStr = testStart.toISOString().split('T')[0];
      const testEndStr = testEnd.toISOString().split('T')[0];

      // Check availability for this alternative date range
      const testAvailability = await checkRequirementsAvailability(
        { [materialType]: { totalQuantity: quantity } },
        testStartStr,
        testEndStr
      );

      if (testAvailability.isAvailable) {
        alternatives.push(testStartStr);
        
        if (alternatives.length >= 5) break; // Limit suggestions
      }
    }

    return alternatives;
  } catch (error) {
    console.error('❌ Error finding alternative dates:', error);
    return [];
  }
};

/**
 * Upgrade reservation status from preliminary to confirmed
 * @param {string} reservationId - Reservation ID to upgrade
 * @returns {Promise<boolean>} Success status
 */
export const upgradeReservationStatus = async (reservationId) => {
  try {
    console.log(`🔄 Upgrading reservation ${reservationId} from preliminary to confirmed`);
    
    // This would integrate with the inventory service
    // For now, we'll just log the action
    console.log(`✅ Reservation ${reservationId} upgraded to confirmed status`);
    
    return true;
  } catch (error) {
    console.error(`❌ Error upgrading reservation ${reservationId}:`, error);
    return false;
  }
};

/**
 * Calculate material requirements from invoice line items
 * @param {Object} invoice - Invoice object with line items
 * @returns {Object} Material requirements object
 */
export const calculateMaterialRequirementsFromInvoice = (invoice) => {
  try {
    if (!invoice || !invoice.lineItems || !Array.isArray(invoice.lineItems)) {
      console.warn('⚠️ No line items found in invoice');
      return {};
    }

    console.log(`📊 Calculating material requirements for ${invoice.lineItems.length} line items`);

    const { requirements } = calculateMaterialRequirements(invoice.lineItems);
    
    console.log(`✅ Calculated requirements for ${Object.keys(requirements).length} material types`);
    
    return requirements;
  } catch (error) {
    console.error('❌ Error calculating material requirements from invoice:', error);
    return {};
  }
};

/**
 * Check if invoice type requires material list
 * @param {string} invoiceType - Invoice type
 * @returns {boolean} Whether material list is required
 */
export const shouldShowMaterialList = (invoiceType) => {
  const typesWithMaterialList = [
    'purchase',
    'rental',
    'rental_extension'
  ];
  
  return typesWithMaterialList.includes(invoiceType);
};

/**
 * Get availability status display properties
 * @param {string} status - Availability status
 * @returns {Object} Display properties (color, label, icon)
 */
export const getAvailabilityStatusDisplay = (status) => {
  return {
    color: AVAILABILITY_STATUS_COLORS[status] || AVAILABILITY_STATUS_COLORS[AVAILABILITY_STATUS.ERROR],
    label: AVAILABILITY_STATUS_LABELS[status] || 'Unknown Status',
    icon: getStatusIcon(status)
  };
};

/**
 * Get icon for availability status
 * @param {string} status - Availability status
 * @returns {string} Icon name
 */
const getStatusIcon = (status) => {
  switch (status) {
    case AVAILABILITY_STATUS.CONFIRMED_AVAILABLE:
      return 'CheckCircleOutlined';
    case AVAILABILITY_STATUS.PRELIMINARY_AVAILABLE:
      return 'InfoCircleOutlined';
    case AVAILABILITY_STATUS.SCHEDULE_PENDING:
      return 'ClockCircleOutlined';
    case AVAILABILITY_STATUS.CONFIRMED_CONFLICT:
    case AVAILABILITY_STATUS.PRELIMINARY_CONFLICT:
      return 'ExclamationCircleOutlined';
    case AVAILABILITY_STATUS.NO_DATES:
      return 'CalendarOutlined';
    case AVAILABILITY_STATUS.ERROR:
    default:
      return 'QuestionCircleOutlined';
  }
};
